import React from 'react';
import { Box, Typography, List, ListItem, ListItemText, Divider, Chip } from '@mui/material';

const RULE_GROUPS = [
  {
    title: 'Non-invasive devices (Rules 1–4)',
    note: 'Non-invasive classification driven by contact type, injured skin, and fluid handling.',
    rules: [
      'Rule 1: Non-invasive devices in contact with injured skin',
      'Rule 2: Non-invasive devices channeling/storing blood or body liquids',
      'Rule 3: Non-invasive devices modifying biological/chemical composition',
      'Rule 4: Non-invasive devices in contact with mucous membrane or injured skin',
    ],
  },
  {
    title: 'Invasive devices (Rules 5–8)',
    note: 'Invasive and surgically invasive devices split by route and duration.',
    rules: [
      'Rule 5: Invasive devices with respect to body orifices (transient/short term)',
      'Rule 6: Surgically invasive devices for transient use',
      'Rule 7: Surgically invasive devices for short-term use',
      'Rule 8: Implantable and long-term surgically invasive devices',
    ],
  },
  {
    title: 'Active devices (Rules 9–12)',
    note: 'Active devices classified by therapeutic/diagnostic intent and energy delivery.',
    rules: [
      'Rule 9: Active therapeutic devices intended to administer energy',
      'Rule 10: Active devices for diagnosis/monitoring',
      'Rule 11: Active devices administering medicines/fluids',
      'Rule 12: Other active devices',
    ],
  },
  {
    title: 'Special rules (Rules 13–22)',
    note: 'Special materials, tissues, contraception, disinfection, and nanomaterials.',
    rules: [
      'Rule 13: Devices incorporating medicinal substances',
      'Rule 14: Devices incorporating animal/human tissues',
      'Rule 15: Devices for contraception or prevention of STD transmission',
      'Rule 16: Devices for blood bags',
      'Rule 17: Devices incorporating non-viable animal tissues',
      'Rule 18: Devices for disinfection or sterilization',
      'Rule 19: Devices for recording or transferring diagnostic images',
      'Rule 20: Devices containing nanomaterials',
      'Rule 21: Devices for the administration of medicines',
      'Rule 22: Active devices with a diagnostic or monitoring function',
    ],
  },
];

const MdrAnnexRulesPage: React.FC = () => {
  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#111827' }}>
        MDR Annex VIII Rules
      </Typography>
      <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
        Grouped summary for classification guidance. Source: MDR Annex VIII.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {RULE_GROUPS.map((group) => (
          <Box key={group.title} sx={{ border: '1px solid #e6e8ec', borderRadius: 2, bgcolor: '#fff' }}>
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #eef0f3' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827' }}>
                {group.title}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                {group.note}
              </Typography>
            </Box>
            <List sx={{ py: 0 }}>
              {group.rules.map((rule, index) => (
                <React.Fragment key={rule}>
                  <ListItem sx={{ py: 1.5, px: 2.5 }}>
                    <ListItemText
                      primary={rule}
                      primaryTypographyProps={{ fontSize: '0.95rem', color: '#111827', fontWeight: 500 }}
                    />
                    <Chip label="Annex VIII" size="small" variant="outlined" />
                  </ListItem>
                  {index < group.rules.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MdrAnnexRulesPage;
